import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Users, Smile, ShieldCheck, BookOpen, MessageSquare, Home as HomeIcon, LogIn, GraduationCap, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { to: '/', label: 'Home', icon: <HomeIcon className="w-4 h-4 mr-1 text-cm-pink" /> },
  { to: '/over-ons', label: 'Over ons', icon: <Users className="w-4 h-4 mr-1 text-cm-yellow" /> },
  { to: '/hoe-werkt-het', label: 'Hoe werkt het?', icon: <Smile className="w-4 h-4 mr-1 text-cm-green" /> },
  { to: '/buddys', label: 'Buddy\'s', icon: <Users className="w-4 h-4 mr-1 text-cm-blue" /> },
  { to: '/veiligheid', label: 'Veiligheid', icon: <ShieldCheck className="w-4 h-4 mr-1 text-cm-pink" /> },
  { to: '/voor-scholen', label: 'Scholen', icon: <BookOpen className="w-4 h-4 mr-1 text-cm-yellow" /> },
  { to: '/trainingen', label: 'Trainingen', icon: <GraduationCap className="w-4 h-4 mr-1 text-cm-green" /> },
  { to: '/roadmap', label: 'Roadmap', icon: <MapPin className="w-4 h-4 mr-1 text-cm-blue" /> },
  { to: '/contact', label: 'Contact', icon: <MessageSquare className="w-4 h-4 mr-1 text-cm-pink" /> },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const activeLinkClass = "text-white font-medium bg-white/20 px-2 py-1 rounded-lg text-xs sm:text-sm";
  const inactiveLinkClass = "text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300 font-medium px-2 py-1 rounded-lg text-xs sm:text-sm";
  
  return (
    <nav 
      className="relative z-50 shadow-xl rounded-b-3xl overflow-hidden bg-gradient-to-r from-cm-blue to-cm-pink"
      style={{
        paddingTop: 'env(safe-area-inset-top)'
      }}
    >
      {/* Enhanced glass effect overlay */}
      <div className="absolute inset-0 bg-black/10 backdrop-blur-sm"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
        
        {/* Mobile Layout - Fixed positioning */}
        <div className="flex lg:hidden items-center justify-between min-h-[80px] sm:min-h-[90px]">
          
          {/* Mobile Menu Button - Left */}
          <Button 
            onClick={toggleMenu} 
            variant="ghost" 
            size="icon" 
            className="text-white hover:text-white hover:bg-white/20 rounded-xl transition-all duration-300 min-w-[48px] min-h-[48px] p-3 z-30"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>

          {/* Logo Section - Mobile - Centered */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-90 transition-all duration-300 p-2 rounded-xl hover:bg-white/10"
          >
            <img 
              src="/lovable-uploads/93f47e9f-abbe-4c7a-af9d-7715deeb50a1.png" 
              alt="ClassBuddy - De AI-buddy voor ieder kind" 
              className="h-16 sm:h-18 w-auto"
            />
          </Link>

          {/* Spacer for balance */}
          <div className="w-[48px]"></div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-3 lg:gap-4 items-center min-h-[90px]">
          
          {/* Logo Section - Desktop - Bigger */}
          <div className="flex justify-start">
            <Link 
              to="/" 
              className="flex items-center hover:opacity-90 transition-all duration-300 p-2 -m-2 rounded-xl hover:bg-white/10"
            >
              <img 
                src="/lovable-uploads/93f47e9f-abbe-4c7a-af9d-7715deeb50a1.png" 
                alt="ClassBuddy - De AI-buddy voor ieder kind" 
                className="h-16 xl:h-18 w-auto"
              />
            </Link>
          </div>

          {/* Navigation - Centered */}
          <div className="flex justify-center">
            <div className="bg-white/10 backdrop-blur-md rounded-full px-2 xl:px-3 py-2 border border-white/20 shadow-lg">
              <div className="flex space-x-0.5 xl:space-x-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) => 
                      `${isActive ? activeLinkClass : inactiveLinkClass} whitespace-nowrap`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          {/* Login Button - Desktop - Purple gradient */}
          <div className="flex justify-end">
            <Button 
              className="bg-gradient-to-r from-cm-blue to-cm-pink hover:from-cm-blue/90 hover:to-cm-pink/90 text-white font-medium px-4 xl:px-6 py-2 xl:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-0 text-sm xl:text-base min-h-[44px]"
            >
              <a href="https://app.classbuddy.nl" target="_blank" rel="noopener noreferrer" className="flex items-center">
                <LogIn size={16} className="mr-1 xl:mr-2" /> Inloggen
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - White Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "-100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "-100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden fixed top-0 left-0 h-screen w-80 shadow-xl z-[100] bg-gradient-to-b from-cm-blue to-cm-pink backdrop-blur-md"
          >
            <div className="flex flex-col h-full">
              {/* Close button */}
              <div className="flex justify-end px-4 py-4">
                <Button 
                  onClick={() => setIsOpen(false)}
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:text-white hover:bg-white/20 rounded-lg w-8 h-8"
                >
                  <X size={20} />
                </Button>
              </div>
              
              {/* Navigation */}
              <div className="flex-1 overflow-hidden py-2">
                <div className="px-4 mb-2">
                  <h3 className="text-white/80 font-medium text-sm mb-3">Menu</h3>
                  <div className="flex flex-col space-y-1">
                    {navLinks.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) => 
                          `${isActive ? 'text-white font-medium bg-white/20' : 'text-white/90 hover:text-white hover:bg-white/10'} 
                          flex items-center py-3 px-3 rounded-lg text-sm transition-all duration-300`
                        }
                      >
                        {link.icon} {link.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Login Button */}
              <div className="px-4 py-4 border-t border-white/20">
                <Button 
                  className="bg-gradient-to-r from-cm-blue to-cm-pink hover:from-cm-blue/90 hover:to-cm-pink/90 text-white font-medium w-full py-3 rounded-lg shadow-lg border-0 text-sm"
                  onClick={() => setIsOpen(false)}
                >
                  <a href="https://app.classbuddy.nl" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full">
                     <LogIn size={14} className="mr-2" /> Inloggen
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;